import React, { useState } from 'react';

import { useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import CrossCircleIcon from '@atlaskit/icon/core/cross-circle';
import SuccessIcon from '@atlaskit/icon/core/status-success';
import { Box, Inline, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { messages } from '../messages';
import { FunctionResultStatus, type FunctionResultType } from '../types';

const functionResultStyles = cssMap({
	functionCard: {
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
		backgroundColor: token('color.background.neutral'),
		borderRadius: token('radius.small'),
	},
	outputContent: {
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
		backgroundColor: token('color.background.neutral.subtle'),
		borderRadius: token('radius.small'),
		maxHeight: '300px',
		overflow: 'auto',
	},
	preformatted: {
		whiteSpace: 'pre-wrap',
		margin: 0,
		fontFamily: token('font.family.code'),
	},
});

export const FunctionResult = ({
	result,
	index,
}: {
	result: FunctionResultType;
	index: number;
}) => {
	const { formatMessage } = useIntl();
	const [isExpanded, setIsExpanded] = useState(false);

	const info = result.pluginInvocationInfo;
	if (!info) {
		return null;
	}

	const isSuccess = info.status === FunctionResultStatus.SUCCESS;

	return (
		<Box xcss={functionResultStyles.functionCard}>
			<Stack space="space.100">
				<Pressable onClick={() => setIsExpanded(!isExpanded)}>
					<Inline space="space.100" alignBlock="center" spread="space-between">
						<Inline space="space.100" alignBlock="center">
							<IconButton
								icon={isExpanded ? ChevronDownIcon : ChevronRightIcon}
								label={formatMessage(
									isExpanded
										? messages.ai_debugger_modal_collapse_label
										: messages.ai_debugger_modal_expand_label,
								)}
								appearance="subtle"
								spacing="compact"
							/>
							<Text weight="medium" size="small">
								{index + 1}. {info.name || 'Unknown Function'}
							</Text>
						</Inline>
						<Inline space="space.050" alignBlock="center">
							{isSuccess ? (
								<SuccessIcon label={info.status || 'Unknown'} color={token('color.icon.success')} />
							) : (
								<CrossCircleIcon
									label={info.status || 'Unknown'}
									color={token('color.icon.danger')}
								/>
							)}
							<Text size="small" color={isSuccess ? 'color.text.success' : 'color.text.danger'}>
								{info.status || 'Unknown'}
							</Text>
						</Inline>
					</Inline>
				</Pressable>
				{isExpanded && info.output && (
					<Box xcss={functionResultStyles.outputContent}>
						<Box as="pre" xcss={functionResultStyles.preformatted}>
							{info.output}
						</Box>
					</Box>
				)}
			</Stack>
		</Box>
	);
};
